import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import payrollService from '../../../services/payrollService';
import employeeService from '../../../services/employeeService';
import clientService from '../../../services/clientService';
import clientSiteService from '../../../services/clientSiteService';
import employeeDeploymentService from '../../../services/employeeDeploymentService';
import offerLettersService from '../../../services/offerLettersService';
import attendanceService from '../../../services/attendanceService';

const calculatePF = (employee, basicSalary) => {
  if (!employee || !basicSalary) return '0';
  const pfType = employee.pf_calculation_type || 'attendance_based';
  const basic = parseFloat(basicSalary) || 0;
  if (pfType === 'none') return '0';
  if (pfType === 'fixed') return (employee.fixed_pf_amount || 0).toString();
  let pfAmount = Math.round(basic * 0.12);
  if (pfType === 'attendance_based_max_amount' && pfAmount > 1800) pfAmount = 1800;
  return pfAmount.toString();
};

const buildCtcBreakdown = (annualCtc, employee) => {
  const annualSalary = parseFloat(annualCtc || 0);
  if (!annualSalary || Number.isNaN(annualSalary)) return null;
  const monthlySalary = annualSalary / 12;
  const basic = Math.round(monthlySalary * 0.45);
  const hra = Math.round(monthlySalary * 0.18);
  const specialAllowance = Math.round(monthlySalary * 0.12);
  const otherAllowance = Math.round(monthlySalary - basic - hra - specialAllowance);
  const employeePf = calculatePF(employee, basic);
  return {
    ctc_annual: annualCtc.toString(),
    basic_salary: basic.toString(),
    hra: hra.toString(),
    special_allowance: specialAllowance.toString(),
    other_allowance: otherAllowance.toString(),
    employee_pf: employeePf,
    professional_tax: '200',
    medical_insurance: '350',
    overtime_pay: '0',
    bonus: Math.round(basic * 0.0833).toString(),
    tax_deduction: '0',
  };
};

const buildSubmitPayload = (formData, employeeId, clientId) => {
  const basic = parseFloat(formData.basic_salary || 0);
  const hra = parseFloat(formData.hra || 0);
  const otherAllowance = parseFloat(formData.other_allowance || 0);
  const specialAllowance = parseFloat(formData.special_allowance || 0);
  const overtime = parseFloat(formData.overtime_pay || 0);
  const bonus = parseFloat(formData.bonus || 0);
  const employeePf = parseFloat(formData.employee_pf || 0);
  const profTax = parseFloat(formData.professional_tax || 0);
  const taxDeduction = parseFloat(formData.tax_deduction || 0);
  const medicalInsurance = parseFloat(formData.medical_insurance || 0);
  const grossSalary = basic + hra + otherAllowance + specialAllowance + overtime + bonus;
  const totalDeductions = employeePf + profTax + taxDeduction + medicalInsurance;
  const netSalary = grossSalary - totalDeductions;

  return {
    employee_id: employeeId,
    client_id: clientId || null,
    client_site_id: formData.client_site_id || null,
    pay_period_start: formData.pay_period_start,
    pay_period_end: formData.pay_period_end,
    basic_salary: basic,
    hra,
    other_allowance: otherAllowance,
    special_allowance: specialAllowance,
    overtime_pay: overtime,
    bonus,
    employee_pf: employeePf,
    professional_tax: profTax,
    tax_deduction: taxDeduction,
    medical_insurance: medicalInsurance,
    allowances: hra + otherAllowance + specialAllowance + overtime + bonus,
    deductions: totalDeductions,
    gross_salary: grossSalary,
    net_salary: netSalary,
    status: formData.status,
  };
};

const getDeploymentClientId = (deployment, clientSites = []) => {
  if (!deployment) return null;
  if (deployment.client_id) return deployment.client_id;
  const site = clientSites.find((s) => s.id === deployment.client_site_id);
  return site?.client_id || null;
};

const formatDeploymentSite = (deployment) => {
  if (!deployment?.client_site_id && !deployment?.site_name) return null;
  const site = deployment.site_name || 'Site';
  const client = deployment.company_name ? ` · ${deployment.company_name}` : '';
  const city = deployment.city ? ` (${deployment.city})` : '';
  return `${site}${client}${city}`;
};

/** Map deployment row to employees.id (handles legacy API where employee_id was overwritten by code) */
const resolveDeploymentEmployeeDbId = (dep, employeeList) => {
  if (!dep) return null;
  const depEmpId = dep.employee_id;
  if (!depEmpId && !dep.employee_code) return null;

  const byUuid = employeeList.find((e) => e.id === depEmpId);
  if (byUuid) return byUuid.id;

  const code = dep.employee_code || depEmpId;
  const byCode = employeeList.find((e) => e.employee_id === code);
  return byCode?.id || depEmpId;
};

const CreatePayrollRecordModal = ({ isOpen, onClose, payrollRecord, onSave, prefillEmployeeId }) => {
  const [creationMode, setCreationMode] = useState('single');
  const [loading, setLoading] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [loadingBulkPreview, setLoadingBulkPreview] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [error, setError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [clientSites, setClientSites] = useState([]);
  const [clientId, setClientId] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [formData, setFormData] = useState({
    ctc_annual: '',
    employee_id: '',
    client_site_id: '',
    pay_period_start: '',
    pay_period_end: '',
    basic_salary: '',
    hra: '',
    other_allowance: '',
    special_allowance: '',
    overtime_pay: '',
    bonus: '',
    employee_pf: '',
    professional_tax: '',
    tax_deduction: '',
    medical_insurance: '',
    status: 'draft'
  });
  const [loadingCtc, setLoadingCtc] = useState(false);
  const [ctcError, setCtcError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadEmployees();
      loadClients();
      loadDeployments();
      loadClientSites();
      setBulkResult(null);
      setBulkPreview([]);
      if (!payrollRecord?.id) {
        setCreationMode('single');
        setSelectedEmployeeIds([]);
        setClientId('');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const employeeId = formData.employee_id;
    if (!employeeId) return;
    if (formData.ctc_annual && parseFloat(formData.ctc_annual) > 0) return;

    const fetchCtc = async () => {
      try {
        setLoadingCtc(true);
        setCtcError(null);
        // NOTE: After "New Hire" deployment, backend updates offer letter status
        // from "accepted" to "Moved Deployment". We must consider both statuses.
        const result = await offerLettersService.getOfferLetters({ employee_id: employeeId });
        const offerLetters = (result?.success && Array.isArray(result.data)) ? result.data : [];
        const eligible = offerLetters.filter(letter => {
          const s = (letter?.status || '').toString().toLowerCase();
          return s === 'accepted' || s === 'moved deployment';
        });

        if (eligible.length > 0) {
          const sorted = [...eligible].sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || a.joining_date || 0).getTime();
            const dateB = new Date(b.updated_at || b.created_at || b.joining_date || 0).getTime();
            return dateB - dateA;
          });
          const latest = sorted[0];
          if (latest?.offered_salary) {
            applyCtcBreakdown(latest.offered_salary.toString());
          } else {
            setCtcError('Accepted offer letter missing CTC value.');
          }
        } else {
          setCtcError('No accepted offer letter found for this employee.');
        }
      } catch (err) {
        setCtcError('Failed to fetch offered CTC.');
      } finally {
        setLoadingCtc(false);
      }
    };

    fetchCtc();
  }, [formData.employee_id]);

  useEffect(() => {
    const employeeId = formData.employee_id;
    if (!employeeId) return;

    const fetchDeploymentDates = async () => {
      try {
        const result = await employeeDeploymentService.getDeployments({
          employee_id: employeeId,
          status: 'active'
        });
        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          const sorted = [...result.data].sort((a, b) => {
            const dateA = new Date(a.deployment_start_date || a.start_date || 0).getTime();
            const dateB = new Date(b.deployment_start_date || b.start_date || 0).getTime();
            return dateB - dateA;
          });
          const latest = sorted[0];
          setFormData(prev => ({
            ...prev,
            client_site_id: latest.client_site_id || prev.client_site_id,
            pay_period_start: prev.pay_period_start || latest.deployment_start_date || latest.start_date || '',
            pay_period_end: prev.pay_period_end || latest.deployment_end_date || latest.end_date || '',
          }));
          const depClientId = getDeploymentClientId(latest, clientSites);
          if (depClientId && !clientId) {
            setClientId(depClientId);
          }
        }
      } catch (err) {
        console.error('Failed to load deployment dates:', err);
      }
    };

    fetchDeploymentDates();
  }, [formData.employee_id, clientSites]);

  useEffect(() => {
    if (!formData.ctc_annual) return;
    applyCtcBreakdown(formData.ctc_annual);
  }, [formData.ctc_annual]);

  useEffect(() => {
    console.log('[CreatePayrollRecordModal] Payroll record changed:', payrollRecord);
    if (payrollRecord?.id) {
      console.log('[CreatePayrollRecordModal] Setting form data from payroll record:', payrollRecord);
      const ctcFromPayroll = payrollRecord.gross_salary
        ? (parseFloat(payrollRecord.gross_salary || 0) * 12)
        : (
          (parseFloat(payrollRecord.basic_salary || 0) || 0) +
          (parseFloat(payrollRecord.hra || 0) || 0) +
          (parseFloat(payrollRecord.other_allowance || 0) || 0) +
          (parseFloat(payrollRecord.special_allowance || 0) || 0) +
          (parseFloat(payrollRecord.overtime_pay || 0) || 0) +
          (parseFloat(payrollRecord.bonus || 0) || 0)
        ) * 12;
      setFormData({
        ctc_annual: ctcFromPayroll ? ctcFromPayroll.toString() : '',
        employee_id: payrollRecord.employee_id || '',
        client_site_id: payrollRecord.client_site_id || '',
        pay_period_start: payrollRecord.pay_period_start || '',
        pay_period_end: payrollRecord.pay_period_end || '',
        basic_salary: payrollRecord.basic_salary?.toString() || '',
        hra: payrollRecord.hra?.toString() || '0',
        other_allowance: payrollRecord.other_allowance?.toString() || '0',
        special_allowance: payrollRecord.special_allowance?.toString() || '0',
        overtime_pay: payrollRecord.overtime_pay?.toString() || '0',
        bonus: payrollRecord.bonus?.toString() || '0',
        employee_pf: payrollRecord.employee_pf?.toString() || '0',
        professional_tax: payrollRecord.professional_tax?.toString() || '0',
        tax_deduction: payrollRecord.tax_deduction?.toString() || '0',
        medical_insurance: payrollRecord.medical_insurance?.toString() || '0',
        status: payrollRecord.status || 'draft'
      });
    } else {
      // Set default dates for new record
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      setFormData({
        ctc_annual: '',
        employee_id: prefillEmployeeId || '',
        client_site_id: '',
        pay_period_start: firstDay.toISOString().split('T')[0],
        pay_period_end: lastDay.toISOString().split('T')[0],
        basic_salary: '',
        hra: '0',
        other_allowance: '0',
        special_allowance: '0',
        overtime_pay: '0',
        bonus: '0',
        employee_pf: '0',
        professional_tax: '0',
        tax_deduction: '0',
        medical_insurance: '0',
        status: 'draft'
      });
    }
    setError(null);
  }, [payrollRecord, isOpen, prefillEmployeeId]);

  const loadEmployees = async () => {
    try {
      const result = await employeeService.getEmployees();
      if (result?.success) {
        const list = (result.data || []).filter((e) => e.status !== 'terminated');
        setEmployees(list);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadClients = async () => {
    try {
      const result = await clientService.getClients();
      if (result?.success) {
        setClients(result.data || []);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const loadDeployments = async () => {
    try {
      const result = await employeeDeploymentService.getDeployments({ status: 'active' });
      let list = result?.success ? (result.data || []) : [];
      if (list.length === 0) {
        const fallback = await employeeDeploymentService.getDeployments();
        if (fallback?.success) list = fallback.data || [];
      }
      setDeployments(list);
    } catch (err) {
      console.error('Error loading deployments:', err);
    }
  };

  const loadClientSites = async () => {
    try {
      const result = await clientSiteService.getClientSites({ is_active: 'true' });
      if (result?.success) {
        setClientSites(result.data || []);
      }
    } catch (err) {
      console.error('Error loading client sites:', err);
    }
  };

  const deploymentByEmployee = React.useMemo(() => {
    const map = new Map();
    deployments.forEach((dep) => {
      const dbEmployeeId = resolveDeploymentEmployeeDbId(dep, employees);
      if (!dbEmployeeId) return;
      const enriched = {
        ...dep,
        client_id: getDeploymentClientId(dep, clientSites),
      };
      const existing = map.get(dbEmployeeId);
      const depDate = new Date(dep.deployment_start_date || 0).getTime();
      if (!existing || depDate > new Date(existing.deployment_start_date || 0).getTime()) {
        map.set(dbEmployeeId, enriched);
      }
    });
    return map;
  }, [deployments, clientSites, employees]);

  const clientFilteredEmployees = React.useMemo(() => {
    const withDeployment = employees.filter((emp) => deploymentByEmployee.has(emp.id));
    if (!clientId) return withDeployment;

    const idsAtClient = new Set();
    deployments.forEach((d) => {
      const dbId = resolveDeploymentEmployeeDbId(d, employees);
      if (!dbId) return;
      if (getDeploymentClientId(d, clientSites) === clientId) {
        idsAtClient.add(dbId);
      }
    });
    return withDeployment.filter((emp) => idsAtClient.has(emp.id));
  }, [employees, deployments, clientId, clientSites, deploymentByEmployee]);

  const selectedDeployment = formData.employee_id
    ? deploymentByEmployee.get(formData.employee_id)
    : null;

  const clientsWithDeployments = React.useMemo(() => {
    const clientIds = new Set();
    deployments.forEach((d) => {
      const cid = getDeploymentClientId(d, clientSites);
      if (cid) clientIds.add(cid);
    });
    return clients.filter((c) => clientIds.has(c.id));
  }, [clients, deployments, clientSites]);

  const selectedClientSiteLabel = React.useMemo(() => {
    if (selectedDeployment?.client_site_id) {
      return formatDeploymentSite(selectedDeployment);
    }
    if (formData.client_site_id) {
      const site = clientSites.find((s) => s.id === formData.client_site_id);
      if (site) {
        return `${site.site_name}${site.company_name ? ` · ${site.company_name}` : ''}`;
      }
    }
    return null;
  }, [selectedDeployment, formData.client_site_id, clientSites]);

  useEffect(() => {
    if (!isOpen || !prefillEmployeeId || payrollRecord?.id) return;
    if (!deployments.length && !employees.length) return;
    const dep = deploymentByEmployee.get(prefillEmployeeId);
    if (dep) {
      setFormData((prev) => ({
        ...prev,
        employee_id: prefillEmployeeId,
        client_site_id: dep.client_site_id || '',
      }));
      const depClientId = getDeploymentClientId(dep, clientSites);
      if (depClientId) setClientId(depClientId);
    }
  }, [isOpen, prefillEmployeeId, payrollRecord?.id, deploymentByEmployee, deployments.length, employees.length, clientSites]);

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );
  };

  const selectAllFilteredEmployees = () => {
    setSelectedEmployeeIds(clientFilteredEmployees.map((emp) => emp.id));
  };

  const generateBulkPreview = async () => {
    if (!formData.pay_period_start || !formData.pay_period_end) {
      setError('Pay period dates are required');
      return;
    }
    if (selectedEmployeeIds.length === 0) {
      setError('Select at least one employee');
      return;
    }

    setLoadingBulkPreview(true);
    setError(null);
    const preview = [];

    for (const employeeId of selectedEmployeeIds) {
      const employee = employees.find((emp) => emp.id === employeeId);
      if (!employee) continue;

      const deployment = deploymentByEmployee.get(employeeId);
      let annualCtc = null;
      try {
        const result = await offerLettersService.getOfferLetters({ employee_id: employeeId });
        const offers = (result?.success && Array.isArray(result.data)) ? result.data : [];
        const eligible = offers.filter((letter) => {
          const s = (letter?.status || '').toLowerCase();
          return s === 'accepted' || s === 'moved deployment';
        });
        if (eligible.length > 0) {
          const latest = [...eligible].sort((a, b) =>
            new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
          )[0];
          annualCtc = parseFloat(latest.offered_salary || 0);
        }
      } catch (_) {
        /* use salary fallback */
      }
      if (!annualCtc && employee.salary) {
        annualCtc = parseFloat(employee.salary) * 12;
      }

      const breakdown = annualCtc ? buildCtcBreakdown(annualCtc, employee) : null;
      const payload = breakdown
        ? buildSubmitPayload(
            { ...formData, ...breakdown, client_site_id: deployment?.client_site_id || '' },
            employeeId,
            clientId || getDeploymentClientId(deployment, clientSites)
          )
        : null;

      preview.push({
        employee_id: employeeId,
        employee_name: employee.full_name,
        employee_code: employee.employee_id,
        client_site_name: formatDeploymentSite(deployment) || '—',
        annual_ctc: annualCtc,
        gross_salary: payload?.gross_salary ?? 0,
        net_salary: payload?.net_salary ?? 0,
        ready: !!payload,
        record: payload,
      });
    }

    setBulkPreview(preview);
    setLoadingBulkPreview(false);
  };

  const handleBulkSubmit = async () => {
    if (bulkPreview.filter((row) => row.ready).length === 0) {
      setError('Generate preview first — no ready records to create');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const readyRecords = bulkPreview.filter((row) => row.ready).map((row) => row.record);
      const result = await payrollService.bulkCreatePayrollRecords({
        pay_period_start: formData.pay_period_start,
        pay_period_end: formData.pay_period_end,
        client_id: clientId || null,
        status: formData.status,
        records: readyRecords,
      });

      if (result?.success) {
        setBulkResult(result.data);
        if ((result.data?.created_count || 0) > 0) {
          onSave?.();
        }
      } else {
        setError(result?.error || 'Bulk create failed');
      }
    } catch (err) {
      setError('An unexpected error occurred during bulk create');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleEmployeeChange = (employeeId) => {
    const deployment = deploymentByEmployee.get(employeeId);
    const deploymentClientId = getDeploymentClientId(deployment, clientSites);

    if (deploymentClientId && clientId && deploymentClientId !== clientId) {
      setClientId(deploymentClientId);
    } else if (deploymentClientId && !clientId) {
      setClientId(deploymentClientId);
    }

    setFormData((prev) => ({
      ...prev,
      employee_id: employeeId,
      client_site_id: deployment?.client_site_id || '',
    }));
    setError(null);

    const selectedEmployee = employees.find((emp) => emp.id === employeeId);
    if (selectedEmployee && formData.basic_salary) {
      const pfAmount = calculatePF(selectedEmployee, formData.basic_salary);
      setFormData((prev) => ({ ...prev, employee_pf: pfAmount }));
    }
  };

  const applyCtcBreakdown = (annualCtc) => {
    const selectedEmployee = employees.find((emp) => emp.id === formData.employee_id);
    const breakdown = buildCtcBreakdown(annualCtc, selectedEmployee);
    if (!breakdown) return;
    setFormData((prev) => ({ ...prev, ...breakdown }));
  };

  const handleCalculateAttendance = async () => {
    if (!formData.employee_id || !formData.pay_period_start || !formData.pay_period_end) {
      setError('Please select employee and pay period first');
      return;
    }

    try {
      setLoadingAttendance(true);
      setError(null);
      const result = await attendanceService.getAttendanceSummary(
        formData.employee_id,
        formData.pay_period_start,
        formData.pay_period_end
      );

      if (result.success) {
        setAttendanceSummary(result.data);
        const { paid_days, total_calendar_days, overtime_hours, overtime_amount, overtime_settings } = result.data;

        if (total_calendar_days > 0) {
          const annualSalary = parseFloat(formData.ctc_annual || 0);
          if (annualSalary > 0) {
            const monthlyBase = annualSalary / 12;
            const actualGross = (monthlyBase / total_calendar_days) * paid_days;

            // Adjust salary components proportionally
            const ratio = paid_days / total_calendar_days;

            const basic = Math.round((monthlyBase * 0.45) * ratio);
            const hra = Math.round((monthlyBase * 0.18) * ratio);
            const specialAllowance = Math.round((monthlyBase * 0.12) * ratio);
            const otherAllowance = Math.round(actualGross - basic - hra - specialAllowance);

            // Get selected employee and calculate PF
            const selectedEmployee = employees.find(emp => emp.id === formData.employee_id);
            const pfAmount = calculatePF(selectedEmployee, basic);

            // Calculate overtime pay from attendance
            let overtimePay = overtime_amount || 0;
            if (overtime_settings && parseFloat(overtime_hours) > 0) {
              const otPayType = overtime_settings.extra_hours_pay_type;
              const otPayAmount = parseFloat(overtime_settings.extra_hours_pay_amount || 0);
              const otHours = parseFloat(overtime_hours);
              // Calculate hourly rate based on current monthlyBase
              const hourlyRate = monthlyBase / 176;

              if (otPayType === 'percentage_of_salary') {
                  overtimePay = Math.round(otHours * hourlyRate * (otPayAmount / 100));
              } else if (otPayType === 'double_time') {
                  overtimePay = Math.round(otHours * hourlyRate * 2);
              } else if (otPayType === 'time_and_half') {
                  overtimePay = Math.round(otHours * hourlyRate * 1.5);
              } else if (otPayType === 'fixed_hourly_rate') {
                  overtimePay = Math.round(otHours * otPayAmount);
              }
            }
            
            // Calculate bonus as 8.33% of Basic Salary
            const bonusAmount = Math.round(basic * 0.0833);

            setFormData(prev => ({
              ...prev,
              basic_salary: basic.toString(),
              hra: hra.toString(),
              special_allowance: specialAllowance.toString(),
              other_allowance: otherAllowance.toString(),
              employee_pf: pfAmount,
              overtime_pay: overtimePay.toString(),
              bonus: bonusAmount.toString()
            }));
          } else {
            // New logic when no CTC is provided, compute overtime from basic_salary and existing allowances
            const basic = parseFloat(formData.basic_salary || 0);
            
            // Get selected employee and calculate PF
            const selectedEmployee = employees.find(emp => emp.id === formData.employee_id);
            const pfAmount = calculatePF(selectedEmployee, basic);

            // Calculate overtime pay from attendance
            let overtimePay = overtime_amount || 0;
            if (overtime_settings && parseFloat(overtime_hours) > 0) {
              const otPayType = overtime_settings.extra_hours_pay_type;
              const otPayAmount = parseFloat(overtime_settings.extra_hours_pay_amount || 0);
              const otHours = parseFloat(overtime_hours);
              // Calculate hourly rate based on total monthly base (basic + allowances)
              const monthlyBase = basic + parseFloat(formData.hra || 0) + parseFloat(formData.special_allowance || 0) + parseFloat(formData.other_allowance || 0);
              const hourlyRate = monthlyBase / 176;

              if (otPayType === 'percentage_of_salary') {
                  overtimePay = Math.round(otHours * hourlyRate * (otPayAmount / 100));
              } else if (otPayType === 'double_time') {
                  overtimePay = Math.round(otHours * hourlyRate * 2);
              } else if (otPayType === 'time_and_half') {
                  overtimePay = Math.round(otHours * hourlyRate * 1.5);
              } else if (otPayType === 'fixed_hourly_rate') {
                  overtimePay = Math.round(otHours * otPayAmount);
              }
            }
            
            // Calculate bonus as 8.33% of Basic Salary
            const bonusAmount = Math.round(basic * 0.0833);

            setFormData(prev => ({
              ...prev,
              employee_pf: pfAmount,
              overtime_pay: overtimePay.toString(),
              bonus: bonusAmount.toString()
            }));
          }
        }
      } else {
        setError(result.error || 'Failed to fetch attendance summary');
      }
    } catch (err) {
      console.error('Error fetching attendance summary:', err);
      setError('An error occurred while fetching attendance summary');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const calculateTotals = () => {
    const basic = parseFloat(formData.basic_salary || 0);
    const hra = parseFloat(formData.hra || 0);
    const otherAllowance = parseFloat(formData.other_allowance || 0);
    const specialAllowance = parseFloat(formData.special_allowance || 0);
    const overtime = parseFloat(formData.overtime_pay || 0);
    const bonus = parseFloat(formData.bonus || 0);

    const employeePf = parseFloat(formData.employee_pf || 0);
    const profTax = parseFloat(formData.professional_tax || 0);
    const taxDeduction = parseFloat(formData.tax_deduction || 0);
    const medicalInsurance = parseFloat(formData.medical_insurance || 0);

    const grossSalary = basic + hra + otherAllowance + specialAllowance + overtime + bonus;
    const totalDeductions = employeePf + profTax + taxDeduction + medicalInsurance;
    const netSalary = grossSalary - totalDeductions;

    return { grossSalary, totalDeductions, netSalary };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('[CreatePayrollRecordModal] Form submitted');
    console.log('[CreatePayrollRecordModal] Payroll record:', payrollRecord);
    console.log('[CreatePayrollRecordModal] Form data:', formData);

    if (!formData.employee_id) {
      setError('Employee is required');
      return;
    }

    if (!formData.pay_period_start || !formData.pay_period_end) {
      setError('Pay period dates are required');
      return;
    }

    if (!formData.basic_salary) {
      setError('Basic salary is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = buildSubmitPayload(
        formData,
        formData.employee_id,
        clientId || selectedDeployment?.client_id
      );

      console.log('[CreatePayrollRecordModal] Submit data:', submitData);
      console.log('[CreatePayrollRecordModal] Is update?', !!payrollRecord);
      console.log('[CreatePayrollRecordModal] Payroll record ID:', payrollRecord?.id);

      const isEdit = !!payrollRecord?.id;
      const result = isEdit
        ? await payrollService.updatePayrollRecord(payrollRecord.id, submitData)
        : await payrollService.createPayrollRecord(submitData);

      console.log('[CreatePayrollRecordModal] Save result:', result);

      if (result?.success) {
        console.log('[CreatePayrollRecordModal] Save successful, calling onSave callback');
        onSave?.();
      } else {
        console.error('[CreatePayrollRecordModal] Save failed:', result?.error);
        setError(result?.error || `Failed to ${isEdit ? 'update' : 'create'} payroll record`);
      }
    } catch (err) {
      console.error('[CreatePayrollRecordModal] Exception during save:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const { grossSalary, totalDeductions, netSalary } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-foreground">
              {payrollRecord?.id ? 'Edit Payroll Record' : 'Create Payroll Record'}
            </h3>
            {!payrollRecord?.id && (
              <p className="text-sm text-muted-foreground mt-1">
                Single employee or bulk create by client deployment
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {!payrollRecord?.id && (
          <div className="flex gap-2 mb-6 p-1 bg-muted rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setCreationMode('single')}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                creationMode === 'single' ? 'bg-background shadow text-foreground font-medium' : 'text-muted-foreground'
              }`}
            >
              Single Employee
            </button>
            <button
              type="button"
              onClick={() => setCreationMode('bulk')}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                creationMode === 'bulk' ? 'bg-background shadow text-foreground font-medium' : 'text-muted-foreground'
              }`}
            >
              Multiple Employees
            </button>
          </div>
        )}

        {bulkResult && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-sm">
            <p className="font-medium text-green-800">
              Created {bulkResult.created_count} record(s)
              {bulkResult.skipped_count > 0 && `, skipped ${bulkResult.skipped_count} (duplicates)`}
              {bulkResult.error_count > 0 && `, ${bulkResult.error_count} error(s)`}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={creationMode === 'bulk' && !payrollRecord?.id ? (e) => { e.preventDefault(); handleBulkSubmit(); } : handleSubmit} className="space-y-6">
          {/* Shared: Client + Period */}
          {!payrollRecord?.id && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">Client (filter employees)</label>
                <select
                  value={clientId}
                  onChange={(e) => {
                    const nextClientId = e.target.value;
                    setClientId(nextClientId);
                    setSelectedEmployeeIds([]);
                    setBulkPreview([]);
                    if (formData.employee_id) {
                      const dep = deploymentByEmployee.get(formData.employee_id);
                      const depClientId = getDeploymentClientId(dep, clientSites);
                      if (nextClientId && depClientId !== nextClientId) {
                        setFormData((prev) => ({ ...prev, employee_id: '', client_site_id: '' }));
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="">All clients with deployments</option>
                  {clientsWithDeployments.map((client) => (
                    <option key={client.id} value={client.id}>{client.company_name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Filters employees by their active deployment client site.
                </p>
              </div>
              <Input
                label="Pay Period Start *"
                type="date"
                value={formData.pay_period_start}
                onChange={(e) => handleInputChange('pay_period_start', e.target.value)}
                required
              />
              <Input
                label="Pay Period End *"
                type="date"
                value={formData.pay_period_end}
                onChange={(e) => handleInputChange('pay_period_end', e.target.value)}
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Initial Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="draft">Draft</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="approved">Approved</option>
                </select>
              </div>
            </div>
          )}

          {creationMode === 'bulk' && !payrollRecord?.id && (
            <div className="space-y-4 border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-foreground">Select Employees</h4>
                  <p className="text-xs text-muted-foreground">
                    {clientId
                      ? `${clientFilteredEmployees.length} deployed at selected client`
                      : `${clientFilteredEmployees.length} employees with active deployment`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllFilteredEmployees}>
                    Select All
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSelectedEmployeeIds([])}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border">
                {clientFilteredEmployees.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No employees found for this client.</p>
                ) : (
                  clientFilteredEmployees.map((emp) => {
                    const dep = deploymentByEmployee.get(emp.id);
                    return (
                      <label key={emp.id} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/40 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEmployeeIds.includes(emp.id)}
                          onChange={() => toggleEmployeeSelection(emp.id)}
                          className="rounded border-border"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{emp.full_name} ({emp.employee_id})</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {formatDeploymentSite(dep) || 'No active deployment'}
                          </p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={generateBulkPreview} disabled={loadingBulkPreview || selectedEmployeeIds.length === 0}>
                  {loadingBulkPreview ? 'Generating...' : `Preview ${selectedEmployeeIds.length} Record(s) from Offer CTC`}
                </Button>
              </div>
              {bulkPreview.length > 0 && (
                <div className="overflow-x-auto border border-border rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Employee</th>
                        <th className="text-left p-2">Site</th>
                        <th className="text-right p-2">Gross</th>
                        <th className="text-right p-2">Net</th>
                        <th className="text-center p-2">Ready</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreview.map((row) => (
                        <tr key={row.employee_id} className="border-t border-border">
                          <td className="p-2">{row.employee_name}</td>
                          <td className="p-2 text-muted-foreground">{row.client_site_name}</td>
                          <td className="p-2 text-right">₹{(row.gross_salary || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right font-medium">₹{(row.net_salary || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-center">{row.ready ? '✓' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {(creationMode === 'single' || payrollRecord?.id) && (
          <>
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Employee *
              </label>
              <select
                value={formData.employee_id}
                onChange={(e) => handleEmployeeChange(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                required
                disabled={!!payrollRecord}
              >
                <option value="">Select Employee</option>
                {clientFilteredEmployees.map((emp) => {
                  const dep = deploymentByEmployee.get(emp.id);
                  const siteLabel = formatDeploymentSite(dep);
                  return (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name} ({emp.employee_id})
                      {siteLabel ? ` — ${siteLabel}` : ''}
                    </option>
                  );
                })}
              </select>
              {clientFilteredEmployees.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No employees with active deployment for this client.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CTC (Annual) *
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={formData.ctc_annual}
                  onChange={(e) => handleInputChange('ctc_annual', e.target.value)}
                  placeholder="Enter annual CTC"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => applyCtcBreakdown(formData.ctc_annual)}
                  disabled={!formData.ctc_annual}
                >
                  Generate
                </Button>
              </div>
              {loadingCtc ? (
                <p className="text-xs text-muted-foreground mt-1">Fetching offered CTC...</p>
              ) : ctcError ? (
                <p className="text-xs text-destructive mt-1">{ctcError}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-generates salary components from the CTC.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Client Site (from deployment)
              </label>
              {selectedClientSiteLabel ? (
                <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/40 text-foreground text-sm">
                  {selectedClientSiteLabel}
                </div>
              ) : (
                <div className="w-full px-3 py-2 border border-dashed border-border rounded-md text-muted-foreground text-sm">
                  {formData.employee_id ? 'No active deployment site found' : 'Select an employee to load deployment site'}
                </div>
              )}
              <input type="hidden" value={formData.client_site_id} readOnly />
            </div>

            {payrollRecord?.id && (
              <>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="draft">Draft</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="locked">Locked</option>
              </select>
            </div>

            <Input
              label="Pay Period Start *"
              type="date"
              value={formData.pay_period_start}
              onChange={(e) => handleInputChange('pay_period_start', e.target.value)}
              required
            />

            <Input
              label="Pay Period End *"
              type="date"
              value={formData.pay_period_end}
              onChange={(e) => handleInputChange('pay_period_end', e.target.value)}
              required
            />
              </>
            )}

            <div className="md:col-span-2 flex items-center justify-between bg-primary/5 p-4 rounded-lg border border-primary/20">
              <div>
                <h4 className="text-sm font-semibold text-primary">Attendance Based Calculation</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Adjust salary components based on present days, Sundays, and holidays.
                </p>
                {attendanceSummary && (
                  <div className="mt-2 flex gap-4 text-xs font-medium">
                    <span className="text-success">Paid Days: {attendanceSummary.paid_days}</span>
                    <span className="text-foreground">Calendar Days: {attendanceSummary.total_calendar_days}</span>
                    <span className="text-muted-foreground">Ratio: {((attendanceSummary.paid_days / attendanceSummary.total_calendar_days) * 100).toFixed(1)}%</span>
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleCalculateAttendance}
                disabled={loadingAttendance || !formData.employee_id}
              >
                {loadingAttendance ? (
                  <>
                    <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Icon name="Calendar" size={16} className="mr-2" />
                    Calculate from Attendance
                  </>
                )}
              </Button>
            </div>
          </div>

          {formData.ctc_annual && (
            <div className="mt-4 bg-muted/40 border border-border rounded-lg p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">CTC Breakdown (Monthly)</span>
                <span className="text-muted-foreground">
                  ₹{Math.round(parseFloat(formData.ctc_annual || 0) / 12).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-xs">
                <div className="flex justify-between">
                  <span>Basic</span>
                  <span>₹{parseFloat(formData.basic_salary || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span>HRA</span>
                  <span>₹{parseFloat(formData.hra || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Special Allowance</span>
                  <span>₹{parseFloat(formData.special_allowance || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other Allowance</span>
                  <span>₹{parseFloat(formData.other_allowance || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span>PF (Employee)</span>
                  <span>₹{parseFloat(formData.employee_pf || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Professional Tax</span>
                  <span>₹{parseFloat(formData.professional_tax || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Medical Insurance</span>
                  <span>₹{parseFloat(formData.medical_insurance || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Earnings */}
          <div className="border-t border-border pt-4">
            <h4 className="text-sm font-semibold text-foreground mb-4">Earnings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Basic Salary *"
                type="number"
                step="0.01"
                value={formData.basic_salary}
                onChange={(e) => handleInputChange('basic_salary', e.target.value)}
                required
              />
              <Input
                label="HRA"
                type="number"
                step="0.01"
                value={formData.hra}
                onChange={(e) => handleInputChange('hra', e.target.value)}
              />
              <Input
                label="Other Allowance"
                type="number"
                step="0.01"
                value={formData.other_allowance}
                onChange={(e) => handleInputChange('other_allowance', e.target.value)}
              />
              <Input
                label="Special Allowance"
                type="number"
                step="0.01"
                value={formData.special_allowance}
                onChange={(e) => handleInputChange('special_allowance', e.target.value)}
              />
              <Input
                label="Overtime Pay"
                type="number"
                step="0.01"
                value={formData.overtime_pay}
                onChange={(e) => handleInputChange('overtime_pay', e.target.value)}
              />
              <Input
                label="Bonus"
                type="number"
                step="0.01"
                value={formData.bonus}
                onChange={(e) => handleInputChange('bonus', e.target.value)}
              />
            </div>
          </div>

          {/* Deductions */}
          <div className="border-t border-border pt-4">
            <h4 className="text-sm font-semibold text-foreground mb-4">Deductions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Employee PF"
                type="number"
                step="0.01"
                value={formData.employee_pf}
                onChange={(e) => handleInputChange('employee_pf', e.target.value)}
              />
              <Input
                label="Professional Tax"
                type="number"
                step="0.01"
                value={formData.professional_tax}
                onChange={(e) => handleInputChange('professional_tax', e.target.value)}
              />
              <Input
                label="Tax Deduction (TDS)"
                type="number"
                step="0.01"
                value={formData.tax_deduction}
                onChange={(e) => handleInputChange('tax_deduction', e.target.value)}
              />
              <Input
                label="Medical Insurance"
                type="number"
                step="0.01"
                value={formData.medical_insurance}
                onChange={(e) => handleInputChange('medical_insurance', e.target.value)}
              />
            </div>
          </div>
          </>
          )}

          {creationMode === 'bulk' && !payrollRecord?.id ? (
            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || bulkPreview.filter((r) => r.ready).length === 0}>
                {loading ? 'Creating...' : `Create ${bulkPreview.filter((r) => r.ready).length} Record(s)`}
              </Button>
            </div>
          ) : (creationMode === 'single' || payrollRecord?.id) && (
          <>
          {/* Summary */}
          <div className="border-t border-border pt-4 bg-muted/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Gross Salary</div>
                <div className="text-lg font-semibold text-foreground">₹{grossSalary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Total Deductions</div>
                <div className="text-lg font-semibold text-foreground">₹{totalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Net Salary</div>
                <div className="text-lg font-semibold text-primary">₹{netSalary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  {payrollRecord ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                payrollRecord ? 'Update Record' : 'Create Record'
              )}
            </Button>
          </div>
          </>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreatePayrollRecordModal;

